/**
 * F1 (04/11) — les statistiques des fiches à confirmer et des écarts d'offre,
 * pour la Direction : combien de cas, combien de temps les patients ont
 * attendu, qui a validé et en combien de temps.
 *
 * L'attente commence à l'arrivée du patient (décision 175). Les noms des
 * chargées sont visibles ; jamais celui d'un patient.
 */
import { z } from 'zod';
import { TOUS_LES_CENTRES } from './pilotage.js';
/** Jour : aujourd'hui ; semaine : les 7 derniers jours ; mois : les 30 derniers, aujourd'hui compris. */
export const PERIODES_STATISTIQUES_CONFIRMATION = ['jour', 'semaine', 'mois'];
export const JOURS_PAR_PERIODE_CONFIRMATION = {
    jour: 1,
    semaine: 7,
    mois: 30,
};
/** Les seuils proposés pour « ouverts depuis plus de N minutes ». */
export const SEUILS_OUVERTS_MINUTES = [15, 30, 60, 120];
export const schemaFiltresStatistiquesConfirmation = z.strictObject({
    periode: z.enum(PERIODES_STATISTIQUES_CONFIRMATION).default('jour'),
    /** Un centre, ou « tous » (insensible à la casse) ; absent : tous. */
    siteId: z
        .string()
        .trim()
        .toLowerCase()
        .pipe(z.union([z.literal(TOUS_LES_CENTRES), z.uuid()]))
        .optional(),
    /** Écrit exactement comme l'un des seuils proposés ; 30 par défaut. */
    plusDe: z
        .enum(['15', '30', '60', '120'])
        .default('30')
        .transform((n) => n === '15' ? 15 : n === '60' ? 60 : n === '120' ? 120 : 30),
});
/** Une durée résumée, en minutes entières ; `null` quand rien n'est mesuré. */
export const schemaDureeResumee = z.strictObject({
    mesures: z.number().int(),
    medianeMin: z.number().int().nullable(),
    maxMin: z.number().int().nullable(),
});
export const schemaStatistiquesConfirmation = z.strictObject({
    genereeLe: z.string(),
    periode: z.enum(PERIODES_STATISTIQUES_CONFIRMATION),
    du: z.string(),
    au: z.string(),
    /** `null` : tous les centres. */
    siteId: z.uuid().nullable(),
    plusDeMinutes: z.number().int(),
    centres: z.array(z.strictObject({ siteId: z.uuid(), siteNom: z.string() })),
    fiches: z.strictObject({
        /** Confirmées ou infirmées par une chargée, arrivées sur la période. */
        traitees: z.number().int(),
        /** Encore à confirmer, arrivées sur la période. */
        enAttente: z.number().int(),
        /** Décidées sans heure de prise en charge (avant U1) : comptées, sans durée. */
        sansDurees: z.number().int(),
        /** De l'arrivée à la première prise en charge. */
        attenteAvantPrise: schemaDureeResumee,
        /** De la première prise en charge à la décision. */
        traitement: schemaDureeResumee,
    }),
    ecarts: z.strictObject({
        tranches: z.number().int(),
        /** Retirés par l'accueil : ni attente mesurée, ni chargée. */
        retires: z.number().int(),
        enAttente: z.number().int(),
        /** De l'arrivée à la décision : le circuit n'a pas d'heure de prise en charge. */
        delaiDecision: schemaDureeResumee,
    }),
    parChargee: z.array(z.strictObject({
        utilisateurId: z.string(),
        nom: z.string(),
        fiches: z.number().int(),
        ecarts: z.number().int(),
        /** Médiane du traitement de ses fiches (prise → décision). */
        traitementMedianMin: z.number().int().nullable(),
    })),
    /** Les cas ouverts depuis plus de `plusDeMinutes`, quelle que soit la période ; les plus anciens d'abord. */
    ouverts: z.array(z.strictObject({
        cle: z.string(),
        type: z.enum(['FICHE', 'ECART']),
        siteNom: z.string().nullable(),
        depuisMin: z.number().int(),
        /** La chargée qui tient la fiche ; `null` : personne (un écart n'a pas de prise). */
        prisPar: z.string().nullable(),
    })),
    ouvertsTotal: z.number().int(),
});
//# sourceMappingURL=statistiques-confirmation.js.map